import {
  CalendarDays,
  Clock3,
  MessageCircle,
  Paperclip,
  UserRound,
} from "lucide-react";
import {
  formatTaskDate,
  formatTaskMinutes,
  getTaskPriorityLabel,
  getTaskStateLabel,
  isTaskOverdue,
  type Task,
} from "../types/tasks";

type Props = {
  task: Task;
  onOpen: (task: Task) => void;
};

export function TaskCard({ task, onOpen }: Props) {
  const overdue = isTaskOverdue(task);

  return (
    <button
      type="button"
      onClick={() => onOpen(task)}
      className="group w-full rounded-[28px] border border-[#dfe8e4] bg-white p-4 text-left shadow-[0_14px_34px_rgba(15,23,42,.055)] transition-all duration-300 hover:-translate-y-1 hover:border-[#00d09c]/24 hover:shadow-[0_24px_54px_rgba(15,23,42,.09)]"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full border border-[#00d09c]/18 bg-[#00d09c]/8 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-[#0b4a2f]">
              {getTaskStateLabel(task.estado)}
            </span>
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-slate-600">
              {getTaskPriorityLabel(task.prioridad)}
            </span>
          </div>

          <h3 className="mt-3 line-clamp-2 text-base font-black tracking-tight text-slate-950">
            {task.titulo}
          </h3>

          <p className="mt-1 line-clamp-2 text-sm font-semibold leading-6 text-slate-500">
            {task.descripcion?.trim() || "Sin descripción ampliada por ahora."}
          </p>
        </div>

        <div
          className={[
            "rounded-2xl border px-3 py-1.5 text-xs font-black shadow-[0_8px_18px_rgba(15,23,42,.04)]",
            overdue
              ? "border-amber-200 bg-amber-50 text-amber-700"
              : "border-[#dfe8e4] bg-white text-slate-500",
          ].join(" ")}
        >
          {overdue ? "Vencida" : "Al día"}
        </div>
      </div>

      <div className="mt-4 grid gap-2 text-sm font-semibold text-slate-600">
        <div className="flex items-center gap-2">
          <CalendarDays size={14} className="text-slate-400" />
          <span>{task.proyecto_nombre ?? "Sin proyecto"} </span>
        </div>

        <div className="flex items-center gap-2">
          <UserRound size={14} className="text-slate-400" />
          <span>{task.asignado_nombre ?? "Sin responsable"}</span>
        </div>

        <div className="flex items-center gap-2">
          <CalendarDays size={14} className="text-slate-400" />
          <span>Fin: {formatTaskDate(task.fecha_fin)}</span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-xs font-black uppercase tracking-[0.12em] text-slate-500 sm:grid-cols-4">
        <Metric label="Subtareas" value={`${task.subtareas_completadas}/${task.subtareas_total}`} />
        <Metric label="Comentarios" value={String(task.comentarios_total)} />
        <Metric label="Adjuntos" value={String(task.adjuntos_total)} />
        <Metric label="Tiempo" value={formatTaskMinutes(task.minutos_total)} />
      </div>

      <div className="mt-4 flex items-center gap-3 text-xs font-bold text-slate-400">
        <span className="inline-flex items-center gap-1">
          <MessageCircle size={13} />
          {task.comentarios_total}
        </span>
        <span className="inline-flex items-center gap-1">
          <Paperclip size={13} />
          {task.adjuntos_total}
        </span>
        <span className="inline-flex items-center gap-1">
          <Clock3 size={13} />
          {formatTaskMinutes(task.minutos_total)}
        </span>
      </div>
    </button>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-[#edf1f3] bg-[#f8fbfc] px-3 py-2">
      <div>{label}</div>
      <div className="mt-1 text-sm text-slate-900">{value}</div>
    </div>
  );
}
